import {
  FileText,
  Tag,
  CheckCircle,
  AlertTriangle,
  Boxes,
  DollarSign,
  Calendar,
  Hash,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/classnames';
import type { ConsolidatedReceivingReport } from '../../types/ai-analysis';

interface ConsolidatedReportProps {readonly report: ConsolidatedReceivingReport;}

function getConfidenceColor(confidence: number): string {
  if (confidence >= 0.8) return 'bg-green-100 text-green-800';
  if (confidence >= 0.5) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

function ConfidenceBadge({ confidence }: {readonly confidence: number;}) {
  return (
    <span className={cn('px-2 py-0.5 text-xs font-medium rounded-full', getConfidenceColor(confidence))}>
      {Math.round(confidence * 100)}%
    </span>
  );
}

export function ConsolidatedReport({ report }: ConsolidatedReportProps) {
  const hasBoxData = report.boxCounting.totalBoxes > 0 || report.boxCounting.totalPallets > 0;
  const hasInvoiceData = report.invoiceData.items.length > 0;
  const hasProductData = report.productVerifications.length > 0;
  const hasConcerns = report.boxCounting.concerns.length > 0;

  return (
    <div className="space-y-4">
      {/* Summary Header */}
      <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-blue-900">Consolidated Receiving Report</h3>
            <p className="text-sm text-blue-700">
              {report.processedImages} of {report.totalImages} images analyzed
            </p>
          </div>
          <div className="text-right text-sm text-blue-600">
            Generated {new Date(report.generatedAt).toLocaleTimeString()}
          </div>
        </div>
      </Card>

      {/* Box Counting Summary */}
      {hasBoxData && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Boxes className="w-5 h-5 text-blue-600" />
            <h4 className="font-semibold">Box Counting Summary</h4>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{report.boxCounting.totalBoxes}</p>
              <p className="text-sm text-gray-600">Total Boxes</p>
            </div>
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-3xl font-bold text-blue-600">{report.boxCounting.totalPallets}</p>
              <p className="text-sm text-gray-600">Pallets</p>
            </div>
          </div>

          {report.boxCounting.allCounts.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">Breakdown by Type</p>
              <div className="space-y-2">
                {report.boxCounting.allCounts.map((count) => (
                  <div key={`${count.itemType}-${count.description}`} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <div>
                      <span className="font-medium">{count.itemType}</span>
                      <span className="text-sm text-gray-500 ml-2">{count.description}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-semibold">{count.count}</span>
                      <ConfidenceBadge confidence={count.confidence} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {report.boxCounting.observations && (
            <div className="grid grid-cols-2 gap-2 text-sm p-3 bg-gray-50 rounded-lg">
              <div><span className="text-gray-500">Condition:</span> <span className="capitalize">{report.boxCounting.observations.condition}</span></div>
              <div><span className="text-gray-500">Stacking:</span> <span className="capitalize">{report.boxCounting.observations.stacking}</span></div>
              <div><span className="text-gray-500">Labeling:</span> <span className="capitalize">{report.boxCounting.observations.labeling}</span></div>
              <div><span className="text-gray-500">Temperature:</span> <span className="capitalize">{report.boxCounting.observations.temperature}</span></div>
            </div>
          )}
        </Card>
      )}

      {/* Concerns Alert */}
      {hasConcerns && (
        <Card className="p-4 bg-amber-50 border-amber-200">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h4 className="font-semibold text-amber-800">Concerns Identified</h4>
          </div>
          <ul className="list-disc list-inside text-sm text-amber-700 space-y-1">
            {report.boxCounting.concerns.map((concern) => (
              <li key={concern}>{concern}</li>
            ))}
          </ul>
        </Card>
      )}

      {/* Invoice Data Summary */}
      {hasInvoiceData && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-green-600" />
            <h4 className="font-semibold">Invoice Data</h4>
          </div>

          {/* Invoice metadata */}
          {(report.invoiceData.invoiceNumbers.length > 0 || report.invoiceData.supplierNames.length > 0) && (
            <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
              {report.invoiceData.invoiceNumbers.length > 0 && (
                <p><span className="text-gray-500">Invoice(s):</span> {report.invoiceData.invoiceNumbers.join(', ')}</p>
              )}
              {report.invoiceData.supplierNames.length > 0 && (
                <p><span className="text-gray-500">Supplier(s):</span> {report.invoiceData.supplierNames.join(', ')}</p>
              )}
            </div>
          )}

          {/* Items table */}
          <div className="mb-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Extracted Items ({report.invoiceData.items.length})</p>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {report.invoiceData.items.map((item) => (
                <div key={`${item.productName}-${item.sku ?? item.lineNumber}`} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.productName}</p>
                    <div className="flex items-center gap-3 text-sm text-gray-500">
                      <span>{item.quantity} {item.unit}</span>
                      {item.unitPrice && <span>@ ${item.unitPrice.toFixed(2)}</span>}
                      {item.lotNumber && (
                        <span className="flex items-center gap-1">
                          <Hash className="w-3 h-3" />{item.lotNumber}
                        </span>
                      )}
                      {item.expiryDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />{item.expiryDate}
                        </span>
                      )}
                    </div>
                  </div>
                  <ConfidenceBadge confidence={item.confidence} />
                </div>
              ))}
            </div>
          </div>

          {/* Totals */}
          {report.invoiceData.totals.total > 0 && (
            <div className="flex justify-end gap-6 pt-3 border-t">
              <div className="text-right">
                <p className="text-xs text-gray-500">Subtotal</p>
                <p className="font-medium">${report.invoiceData.totals.subtotal.toFixed(2)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Tax</p>
                <p className="font-medium">${report.invoiceData.totals.tax.toFixed(2)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total</p>
                <p className="text-lg font-semibold text-green-600">
                  <DollarSign className="w-4 h-4 inline" />
                  {report.invoiceData.totals.total.toFixed(2)}
                </p>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Product Verifications */}
      {hasProductData && (
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <Tag className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold">Product Verifications ({report.productVerifications.length})</h4>
          </div>

          <div className="space-y-3">
            {report.productVerifications.map((product) => (
              <div key={`${product.productName}-${product.lotNumber ?? product.barcode ?? ''}`} className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium">{product.productName}</p>
                  <ConfidenceBadge confidence={product.confidence} />
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {product.lotNumber && <p><span className="text-gray-500">Lot:</span> {product.lotNumber}</p>}
                  {product.expiryDate && <p><span className="text-gray-500">Expiry:</span> {product.expiryDate}</p>}
                  {product.brand && <p><span className="text-gray-500">Brand:</span> {product.brand}</p>}
                  {product.countryOfOrigin && <p><span className="text-gray-500">Origin:</span> {product.countryOfOrigin}</p>}
                </div>
                {product.allergens.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {product.allergens.map((allergen) => (
                      <span key={allergen} className="px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded">
                        {allergen}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Success indicator */}
      {report.processedImages === report.totalImages && report.processedImages > 0 && (
        <div className="flex items-center justify-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="font-medium text-green-800">All images processed successfully</span>
        </div>
      )}
    </div>
  );
}
